// Shared constants. No component defines its own copy of these.

// Product name (spec §1). Rendered as PLAIN TEXT — spec §5 forbids a logo
// image anywhere in the app, so there is no mark to pair this with.
export const APP_NAME = "Easy PostgreSQL";
export const APP_NAME_SUFFIX = "for Zoho CRM";
export const APP_NAME_FULL = `${APP_NAME} ${APP_NAME_SUFFIX}`;

// How often a module's incremental sync job runs. `total_in_minutes` is what
// the v3 scheduler stores as `execute_in`; the label is what the UI shows.
// Spec §5/§8.2: 10 minutes through 24 hours.
export const SYNC_DURATIONS = [
  { label: "10 Mins", total_in_minutes: 10 },
  { label: "15 Mins", total_in_minutes: 15 },
  { label: "30 Mins", total_in_minutes: 30 },
  { label: "1 Hours", total_in_minutes: 60 },
  { label: "2 Hours", total_in_minutes: 120 },
  { label: "3 Hours", total_in_minutes: 180 },
  { label: "4 Hours", total_in_minutes: 240 },
  { label: "6 Hours", total_in_minutes: 360 },
  { label: "8 Hours", total_in_minutes: 480 },
  { label: "12 Hours", total_in_minutes: 720 },
  { label: "24 Hours", total_in_minutes: 1440 },
];

export const DEFAULT_SYNC_DURATION = SYNC_DURATIONS[0];

// Derives the display label for an arbitrary minute count, matching the
// labels above so a value restored from a saved setting renders identically.
export const syncDurationLabel = (minutes) => {
  const mins = Number(minutes);
  if (!Number.isFinite(mins) || mins <= 0) return "";
  const hours = mins / 60;
  return Number.isInteger(hours) ? `${hours} Hours` : `${mins} Mins`;
};

// Resolves a saved minute count back to a SYNC_DURATIONS entry, synthesizing
// one if a setting was saved with a cadence no longer in the list.
export const syncDurationFromMinutes = (minutes) =>
  SYNC_DURATIONS.find((d) => d.total_in_minutes === Number(minutes)) || {
    label: syncDurationLabel(minutes),
    total_in_minutes: Number(minutes),
  };

// v3 job modes. These travel in `body.type` and each Deluge worker refuses
// jobs of the wrong type, so one function cannot eat another's work (§9).
export const JOB_TYPES = {
  SYNC: "sync",
  BACKFILL: "backfill",
  RESTORE: "restore",
};

// The CRM API version each Deluge function is invoked on. Backfill runs on v7;
// everything else on v2. A mismatch means a 404 at fire time (§12.9).
export const JOB_API_VERSION = {
  [JOB_TYPES.SYNC]: "v2",
  [JOB_TYPES.BACKFILL]: "v7",
  [JOB_TYPES.RESTORE]: "v2",
};

// Backfill and restore drain as fast as the scheduler allows; sync uses the
// cadence the customer picked.
export const JOB_EXECUTE_IN = {
  [JOB_TYPES.BACKFILL]: 1,
  [JOB_TYPES.RESTORE]: 1,
};

// Workers parse this by string surgery — the shape is load-bearing (§12.6).
export const LAST_FETCHED_TIME_FORMAT = "YYYY-MM-DD HH:mm:ssZ";

// Fresh sync jobs start their incremental cursor at the epoch, in exactly
// the yyyy-MM-dd HH:mm:ss±HH:mm shape the workers string-parse.
export const INITIAL_LAST_FETCHED_TIME = "1970-01-01 00:00:00+00:00";

// Backup/Restore CSV export chunk size (§5.4).
export const CSV_CHUNK_SIZE = 20000;

// Default Postgres port (§6).
export const DEFAULT_PG_PORT = "5432";

// Fields a user should almost never sync without — Leads-style modules keep
// the person's name here, so omitting it yields nameless rows (§12.8).
export const DEFAULT_SELECTED_FIELDS = ["Last_Name", "Full_Name"];
